<template>
  <div
    class="company-card"
    :class="{
      'company-card-clickable': companyId
    }"
    :role="companyId ? 'link' : undefined"
    :tabindex="companyId ? 0 : -1"
    @click="openCompany"
    @keydown.enter="openCompany"
    @keydown.space.prevent="openCompany"
  >
    <img
      :src="logoUrl"
      :alt="`Логотип ${name}`"
      class="company-logo"
    />

    <div class="company-info">
      <div
        class="company-name"
        :title="name"
      >
        {{ name || "Без названия" }}
      </div>

      <div
        class="company-specifics"
        :title="specifics"
      >
        {{
          specifics ||
          "Специализация не указана"
        }}
      </div>
    </div>

    <div
      v-if="companyId"
      class="company-arrow"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M7.5 5.5L12 10L7.5 14.5"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";

const router = useRouter();

const props = defineProps({
  companyId: {
    type: String,
    default: ""
  },

  name: {
    type: String,
    default: ""
  },

  specifics: {
    type: String,
    default: ""
  },

  logoUrl: {
    type: String,
    default: ""
  }
});

function openCompany() {
  if (!props.companyId) {
    console.warn(
      "⚠️ Невозможно открыть компанию: companyId отсутствует"
    );

    return;
  }

  console.log(
    "🏢 Открываю компанию:",
    props.companyId
  );

  router.push(
    `/company/${props.companyId}`
  );
}
</script>

<style scoped>
.company-card {
  position: relative;

  width: 220px;
  min-height: 72px;

  display: flex;
  align-items: flex-start;

  gap: 11px;

  padding:
    12px
    28px
    12px
    12px;

  margin-bottom: 12px;

  box-sizing: border-box;

  overflow: hidden;

  background:
    linear-gradient(
      145deg,
      #fafafa 0%,
      #f6f7f8 100%
    );

  border:
    1px solid
    rgba(25, 35, 50, 0.065);

  border-radius: 15px;

  box-shadow:
    0 5px 16px
    rgba(20, 30, 50, 0.035);

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.company-card-clickable {
  cursor: pointer;
}

.company-card-clickable:hover {
  transform:
    translateY(-2px);

  background:
    #ffffff;

  border-color:
    rgba(40, 135, 247, 0.18);

  box-shadow:
    0 9px 22px
    rgba(20, 30, 50, 0.075);
}

.company-card-clickable:active {
  transform:
    translateY(0)
    scale(0.992);
}

.company-card-clickable:focus-visible {
  outline:
    2px solid
    rgba(40, 135, 247, 0.28);

  outline-offset: 2px;
}

/* --------------------------------------------------------- */
/* LOGO */
/* --------------------------------------------------------- */

.company-logo {
  width: 40px;
  height: 40px;

  flex-shrink: 0;

  margin-top: 1px;

  border-radius: 50%;

  object-fit: cover;

  background:
    #ffffff;

  border:
    1px solid
    rgba(0, 0, 0, 0.05);

  box-shadow:
    0 3px 8px
    rgba(20, 30, 50, 0.05);
}

/* --------------------------------------------------------- */
/* CONTENT */
/* --------------------------------------------------------- */

.company-info {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 5px;
}

.company-name {
  display:
    -webkit-box;

  overflow: hidden;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp: 2;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;
  font-weight: 700;
  line-height: 1.28;

  letter-spacing:
    -0.12px;

  color: #26292e;

  word-break:
    break-word;

  overflow-wrap:
    anywhere;
}

.company-specifics {
  display:
    -webkit-box;

  overflow: hidden;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp: 3;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
  font-weight: 400;
  line-height: 1.38;

  letter-spacing:
    -0.02px;

  color: #7f858d;

  word-break:
    break-word;

  overflow-wrap:
    anywhere;
}

/* --------------------------------------------------------- */
/* ARROW */
/* --------------------------------------------------------- */

.company-arrow {
  position: absolute;

  top: 50%;
  right: 8px;

  width: 18px;
  height: 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  transform:
    translateY(-50%);

  color:
    rgba(75, 85, 100, 0.42);

  transition:
    color 0.18s ease,
    transform 0.18s ease;
}

.company-arrow svg {
  width: 15px;
  height: 15px;
}

.company-card-clickable:hover
.company-arrow {
  color:
    #2887f7;

  transform:
    translateY(-50%)
    translateX(1px);
}

/* --------------------------------------------------------- */
/* DARK */
/* --------------------------------------------------------- */

html[data-theme="dark"] .company-card {
  background:
    linear-gradient(
      145deg,
      #25272c 0%,
      #212328 100%
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0 6px 18px
    rgba(0, 0, 0, 0.16);
}

html[data-theme="dark"]
.company-card-clickable:hover {
  background:
    linear-gradient(
      145deg,
      #2a2c32 0%,
      #25272c 100%
    );

  border-color:
    rgba(80, 153, 255, 0.22);

  box-shadow:
    0 9px 24px
    rgba(0, 0, 0, 0.24);
}

html[data-theme="dark"] .company-logo {
  background:
    #303238;

  border-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    0 3px 9px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .company-name {
  color:
    #f1f2f4;
}

html[data-theme="dark"] .company-specifics {
  color:
    rgba(255, 255, 255, 0.56);
}

html[data-theme="dark"] .company-arrow {
  color:
    rgba(255, 255, 255, 0.3);
}

html[data-theme="dark"]
.company-card-clickable:hover
.company-arrow {
  color:
    #72b5ff;
}

/* --------------------------------------------------------- */
/* REDUCED MOTION */
/* --------------------------------------------------------- */

@media (prefers-reduced-motion: reduce) {
  .company-card,
  .company-arrow {
    transition: none;
  }

  .company-card-clickable:hover {
    transform: none;
  }

  .company-card-clickable:hover
  .company-arrow {
    transform:
      translateY(-50%);
  }
}
</style>